import {createStore, filterNil} from '@ngneat/elf';
import {AksjeeierbokId, AksjeeierbokModell, AksjeeierbokRessursClient, AksjeeierEnkelModell, AksjeeierId, AksjeeierModell, AksjeeierRessursClient, OppdaterAksjeeierSpesifikasjon, OppdaterBeholdningerSpesifikasjon, OppdaterBeholdningKommentarSpesifikasjon, OpprettAksjeeiereOgBeholdningerSpesifikasjon, OpprettAksjeeierSpesifikasjon, SlettSisteBeholdningsendringModell} from "../ressurser/generertNg2HttpKlient";
import {selectEntity, selectManyByPredicate, withEntities} from "@ngneat/elf-entities";
import {firstValueFrom, Observable} from "rxjs";
import {Injectable} from "@angular/core";
import {tap} from "rxjs/operators";
import {erstatteAlleEntiteterMedForelder, FeilhåndteringArgumenter, Feilhåndteringsstrategi, forelderIdPredikat, MedForelderId, oppdaterEntitetMedForelder, registrerEntiteterMedForelder, registrerEntitetMedForelder, serverFraLagerOgRequestPakketIMeta, serverFraRequest, serverFraRequestPakketIMeta, somFeilhåndteringsstrategiForDataOgMeta, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "felles-ng2/tjenester/datalager/datatjeneste";
import {AksjeklasseDatatjeneste} from "felles-ng2/tjenester/datalager/AksjeklasseDatatjeneste";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {HttpResponse} from "@angular/common/http";
import {DataOgMeta, mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {Temporal} from "temporal-polyfill";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {skrivPlainDateSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import {sorterAksjeeier} from "felles-ng2/modell/aksjeeierModellHjelpere";
import {sorterLevertSamling} from "felles/ng2/rxjsHjelpere";
import PlainDate = Temporal.PlainDate;

const debug = initDebugger('AksjeeierDatatjeneste');

/**
 * Datalager for {@link AksjeeierEnkelModell} og {@link AksjeeierModell}.
 */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class AksjeeierDatatjeneste {

    /** Lager for {@link AksjeeierModell}. Bruker {@link AksjeeierbokId} som forelder */
    private aksjeeierLager = createStore(
      {name: 'aksjeeier'},
      withEntities<AksjeeierModell>());

    /** Lager for {@link AksjeeierModell}. Bruker {@link AksjeeierbokId} som forelder */
    private aksjeeierEnkelLager = createStore(
      {name: 'aksjeeierEnkel'},
      withEntities<AksjeeierEnkelModell>());

    private _aktivAksjeeierbokId: AksjeeierbokId | null = null;

    constructor(private aksjeklasseDatatjeneste: AksjeklasseDatatjeneste,
                private aksjeeierRessurs: AksjeeierRessursClient,
                private aksjeeierbokRessurs: AksjeeierbokRessursClient,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser) {
    }

    get aktivAksjeeierbokId(): AksjeeierbokId | null {
        return this._aktivAksjeeierbokId;
    }

    set aktivAksjeeierbokId(aktiv: AksjeeierbokId) {
        this._aktivAksjeeierbokId = aktiv;
    }

    hentMedMeta(id: AksjeeierId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<AksjeeierModell>> {
        return serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.aksjeeierRessurs.hent(id),
            registrerILager: (respons: HttpResponse<AksjeeierModell>) => {
                registrerEntitetMedForelder(this.aksjeeierEnkelLager, respons.body!, aksjeeier => aksjeeier.aksjeeierbokId);
                return registrerEntitetMedForelder(this.aksjeeierLager, respons.body!, aksjeeier => aksjeeier.aksjeeierbokId)
            },
            hentFraLager: () => this.aksjeeierLager.pipe(selectEntity(id), filterNil()),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente aksjeeier', 'hentMedMeta')
        });
    }

    hent(id: AksjeeierId, feilhåndteringsstrategi = Feilhåndteringsstrategi.visFeilmelding): Observable<AksjeeierModell> {
        return this.hentMedMeta(id, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    finnAksjeeiereTilknyttetMedMeta(aksjeeierbokId: AksjeeierbokId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<AksjeeierEnkelModell[]>> {
        return serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.aksjeeierbokRessurs.hent(aksjeeierbokId),
            registrerILager: (respons: HttpResponse<AksjeeierbokModell>) => {
                this.aksjeklasseDatatjeneste.registrerILager(respons.body!.aksjeklasser, respons.body!.id);
                //registrerEntiteterMedForelder dytter data inn i datalageret vårt, som i sin tur leverer disse via cachestrøm
                return this.registererIAksjeeierEnkelLager(respons);
            },
            hentFraLager: () => {
                return this.aksjeeierEnkelLager.pipe(
                  selectManyByPredicate(forelderIdPredikat(aksjeeierbokId)),
                  sorterLevertSamling(standardSortering));
            },
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n("Kunne ikke hente aksjeeierbok")), 'finnAksjeeiereTilknyttet')
        });
    }

    finnAksjeeiereTilknyttet(aksjeeierbokId: AksjeeierbokId, feilhåndteringsstrategi = Feilhåndteringsstrategi.visFeilmelding): Observable<AksjeeierEnkelModell[]> {
        return this.finnAksjeeiereTilknyttetMedMeta(aksjeeierbokId, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    /** Returnerer data hentet vhja {@link AksjeeierbokRessursClient#hentSlettSisteBeholdningsendringInfo}. */
    hentSlettSisteBeholdningsendringInfo(aksjeeierbokId: AksjeeierbokId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<SlettSisteBeholdningsendringModell> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.aksjeeierbokRessurs.hentSlettSisteBeholdningsendringInfo(aksjeeierbokId).råRespons()
              .pipe(tap(respons => {
                  const modell = respons.body!;
                  registrerEntiteterMedForelder(this.aksjeeierEnkelLager, modell.aksjeeiere.map(eier => eier.aksjeeier), aksjeeierbokId)
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente aksjeeierbok', 'hentSlettSisteBeholdningsendringInfo')
        }));
    }

    oppdater(aksjeeierId: AksjeeierId, spesifikasjon: OppdaterAksjeeierSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<AksjeeierModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.aksjeeierRessurs.oppdater(aksjeeierId, spesifikasjon).råRespons()
              .pipe(tap(respons => {
                  const oppdatertAksjeeier = respons.body!;
                  oppdaterEntitetMedForelder(this.aksjeeierEnkelLager, oppdatertAksjeeier, oppdatertAksjeeier.aksjeeierbokId);
                  oppdaterEntitetMedForelder(this.aksjeeierLager, oppdatertAksjeeier, oppdatertAksjeeier.aksjeeierbokId);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere aksjeeier')), 'oppdater')
        }));
    }

    oppdaterKommentarPåNåværendeBeholdning(aksjeeierId: AksjeeierId, spesifikasjon: OppdaterBeholdningKommentarSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<AksjeeierEnkelModell>> {
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.aksjeeierRessurs.oppdaterKommentarPåNåværendeBeholdning(aksjeeierId, spesifikasjon).råRespons()
              .pipe(tap(respons => {
                  const oppdatertAksjeeier = respons.body! as AksjeeierModell; //uten denne as AksjeeierModell får vi ts feil - vanskelig å skjønner da oppdaterKommentarPåNåværendeBeholdning ret AksjeeierModell
                  oppdaterEntitetMedForelder(this.aksjeeierEnkelLager, oppdatertAksjeeier, oppdatertAksjeeier.aksjeeierbokId);
                  oppdaterEntitetMedForelder(this.aksjeeierLager, oppdatertAksjeeier, oppdatertAksjeeier.aksjeeierbokId);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke oppdatere kommentar på beholdning', 'oppdaterKommentar')
        }))
    }

    opprett(aksjeeierbokId: AksjeeierbokId, spesifikasjon: OpprettAksjeeierSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<AksjeeierModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.aksjeeierRessurs.opprett(aksjeeierbokId, spesifikasjon).råRespons()
              .pipe(
                tap(respons => {
                    const aksjeeier = respons.body!;
                    registrerEntitetMedForelder(this.aksjeeierEnkelLager, aksjeeier, aksjeeierbokId);
                    registrerEntitetMedForelder(this.aksjeeierLager, aksjeeier, aksjeeierbokId);
                })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere aksjeeier')), 'opprett')
        }));
    }

    oppdaterBeholdninger(aksjeeierbokId: AksjeeierbokId, spesifikasjon: OppdaterBeholdningerSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<AksjeeierbokModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.aksjeeierbokRessurs.oppdaterBeholdninger(aksjeeierbokId, spesifikasjon).råRespons()
              .pipe(this.tapOgRegistererIAksjeeierEnkelLager()),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere beholdning')), 'oppdaterBeholdninger')
        }));
    }

    slettSisteBeholdningsendring(aksjeeierbokId: AksjeeierbokId, innføringsdatoSomSlettes: PlainDate, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<AksjeeierbokModell> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.aksjeeierbokRessurs.slettSisteBeholdningsendring(aksjeeierbokId, {innfoeringsdatoSomSlettes: skrivPlainDateSerialisert(innføringsdatoSomSlettes)}).råRespons()
              .pipe(this.tapOgRegistererIAksjeeierEnkelLager()),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke angre siste beholdningsoppdatering')), 'slettSisteBeholdningsendring')
        }));
    }

    opprettAksjeeiereOgBeholding(aksjeeierbokId: AksjeeierbokId, opprettAksjeeiereOgBeholdningerSpesifikasjon: OpprettAksjeeiereOgBeholdningerSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta) {
        debug("opprettAksjeeiereOgBeholding:", aksjeeierbokId, opprettAksjeeiereOgBeholdningerSpesifikasjon)
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres

        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.aksjeeierbokRessurs.opprettAksjeeiereOgBeholding(aksjeeierbokId, opprettAksjeeiereOgBeholdningerSpesifikasjon).råRespons()
              .pipe(this.tapOgRegistererIAksjeeierEnkelLager()),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere beholdning')), 'opprettAksjeeiereOgBeholding')
        }));
    }

    //hjelpemetoder

    private tapOgRegistererIAksjeeierEnkelLager() {
        return tap((respons: HttpResponse<AksjeeierbokModell>) =>
          this.registererIAksjeeierEnkelLager(respons));
    }

    /** Erstatter aksjeeiere registrert i aksjeeierEnkelLager tilknyttet aksjeeierbok.id med aksjeeierbok.aksjeeierbok i angitt respons */
    private registererIAksjeeierEnkelLager(respons: HttpResponse<AksjeeierbokModell>): (AksjeeierEnkelModell & MedForelderId)[] {
        return erstatteAlleEntiteterMedForelder(this.aksjeeierEnkelLager, respons.body!.aksjeeiere, respons.body!.id);
    }

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(feilhåndteringsstrategi: STRATEGI,
                                                                               brukerrettetFeilmelding: string,
                                                                               metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

    static sorterAksjeeierFunksjon() {
        return (a: AksjeeierEnkelModell, b: AksjeeierEnkelModell) => a.navn.toLocaleLowerCase().localeCompare(b.navn.toLocaleLowerCase())
    }

}

const standardSortering = sorterAksjeeier;
